import React, { useState } from "react"
import i18n from "i18n"
import { TextField, Grid, Button, Typography, Box } from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import PropTypes from "prop-types"
import AttachedFilesList from "./AttachedFilesList"
import ConfirmationModal from "common/ConfirmationModal"
import useAttachedFiles from "../../hooks/useAttachedFiles"
import { useDeleteExpenseFile } from "../../hooks/useOrganizationExpenses"
import { API } from "aws-amplify"
import { useAuthContext } from "context/auth/authContext"

const GeneralDataSection = ({ formData, handleInputChange }) => {
  const { user } = useAuthContext()
  const organizationId = user["custom:organizationId"]
  const expenseId = formData.expenseId
  const {
    attachedFiles,
    handleFileSelect,
    handleRemoveFile: removeUploadedFile,
    handleDownloadFile: downloadUploadedFile,
    formatFileSize,
  } = useAttachedFiles([], {
    organizationId,
    expenseId,
    createdByProject: formData.createdByProject,
  })

  const handleDownloadDocument = async file => {
    try {
      const response = await API.get("CORE", "/expenses/get_file", {
        queryStringParameters: {
          organizationId,
          fileId: file.new_filename,
        },
      })
      const signedUrl = response.signed_url
      if (signedUrl) {
        const fileResponse = await fetch(signedUrl)
        const blob = await fileResponse.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = file.original_filename || "archivo"
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      } else {
        console.error("No se recibió signed_url")
      }
    } catch (e) {
      console.error("Error al obtener archivo", e)
    }
  }

  const [downloadingFileId, setDownloadingFileId] = useState(null)
  const allFiles = [
    ...attachedFiles,
    ...(formData.files || []).map(file => ({
      ...file,
      isExistingDocument: true,
      isDownloading: downloadingFileId === (file.id || file.new_filename),
    })),
  ]

  const deleteExpenseFileHook = useDeleteExpenseFile()

  const [fileToDelete, setFileToDelete] = useState(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [deleteFileLoading, setDeleteFileLoading] = useState(false)

  const requestDeleteFile = fileId => {
    setDeleteFileLoading(false)
    setFileToDelete(fileId)
    setConfirmOpen(true)
  }

  const cancelDelete = () => {
    if (deleteFileLoading) return
    setFileToDelete(null)
    setConfirmOpen(false)
  }

  const confirmDelete = async () => {
    if (!fileToDelete) {
      setConfirmOpen(false)
      setFileToDelete(null)
      return
    }
    const fileId = fileToDelete
    const fileObj = (formData.files || []).find(
      file => file.id === fileId || file.new_filename === fileId,
    )
    const isExistingDoc = !!fileObj
    if (isExistingDoc) {
      setDeleteFileLoading(true)
      try {
        await deleteExpenseFileHook({
          organizationId,
          expenseId,
          fileId: fileObj.new_filename,
        })

        const updatedFiles = (formData.files || []).filter(
          file => file.new_filename !== fileObj.new_filename,
        )
        handleInputChange("files")({ target: { value: updatedFiles } })
        setConfirmOpen(false)
        setFileToDelete(null)
      } catch (e) {
        console.error("Error al eliminar archivo", e)
      } finally {
        setDeleteFileLoading(false)
      }
    } else {
      removeUploadedFile(fileId)
      setConfirmOpen(false)
      setFileToDelete(null)
    }
  }

  const handleDownloadFile = async file => {
    if (file.isExistingDocument) {
      setDownloadingFileId(file.id || file.new_filename)
      try {
        await handleDownloadDocument(file)
      } finally {
        setDownloadingFileId(null)
      }
    } else {
      downloadUploadedFile(file)
    }
  }

  return (
    <>
      <Grid item xs={12}>
        <Typography sx={{ fontWeight: "bold" }}>
          {i18n.t("costs.modal.generalData")}
        </Typography>
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label={i18n.t("costs.columns.externalId")}
          value={formData.id_external || ""}
          onChange={handleInputChange("id_external")}
          variant="outlined"
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label={i18n.t("costs.columns.date")}
          type="date"
          value={formData.date}
          onChange={handleInputChange("date")}
          required
          inputProps={{
            max: new Date().toISOString().split("T")[0],
          }}
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={i18n.t("costs.columns.title")}
          required
          value={formData.title}
          onChange={handleInputChange("title")}
          variant="outlined"
        />
      </Grid>
      {formData.purchaseOrderId && (
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label={i18n.t("costs.columns.purchaseOrderNumber")}
            value={formData.purchaseOrderNumber || ""}
            variant="outlined"
            InputProps={{
              readOnly: true,
            }}
          />
        </Grid>
      )}
      <Grid item xs={12} md={formData.purchaseOrderId ? 9 : 12}>
        <TextField
          fullWidth
          label={i18n.t("costs.columns.notes")}
          value={formData.notes}
          onChange={handleInputChange("notes")}
          variant="outlined"
        />
      </Grid>
      <Grid item xs={12} md={12}>
        <Button startIcon={<AddIcon />} onClick={handleFileSelect}>
          {i18n.t("costs.modal.addAttachedFiles")}
        </Button>
      </Grid>
      <AttachedFilesList
        attachedFiles={allFiles}
        onRemoveFile={requestDeleteFile}
        onDownloadFile={handleDownloadFile}
        formatFileSize={formatFileSize}
      />
      <ConfirmationModal
        open={confirmOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title={i18n.t("costs.modal.deleteFileTitle")}
        message={i18n.t("costs.modal.deleteFileMessage")}
        confirmButtonText={i18n.t("buttons.delete")}
        confirmButtonColor="error"
        confirmLoading={deleteFileLoading}
      />
    </>
  )
}

GeneralDataSection.propTypes = {
  formData: PropTypes.object.isRequired,
  handleInputChange: PropTypes.func.isRequired,
}

export default GeneralDataSection
